import React from 'react';
import PropTypes from 'prop-types';
import { Button, Select } from './UIComponents';
import Icon from '@mdi/react';
import { mdiClipboardTextOutline, mdiMicroscope, mdiChevronDown, mdiChevronRight, mdiToolbox } from '@mdi/js';
import { FaCheck } from 'react-icons/fa6';
import { Snackbar, Alert } from '@mui/material';
import { STATES, NON_TOBACCO } from '../../constants/App';
import { mapSolveTypeEnumToStrings } from '../../constants/AnnuitiesSolveTypes';
import { QUOTER_TABS } from '../../constants/QuoterTabs';
import HealthAnalyzerDetailModal from './HealthAnalyzerDetailModal';
import ButtonMenuModal from '../shared/ButtonMenuModal';
import Modal from '../shared/Modal';
import NeedsAnalysisModal from '../alq/NeedsAnalysisModal';
// Form section components
import { ProposedInsuredSection, CommonFields, LifeDemographicsSection, AnnuityProductSection, AnnuityPlanSection, LifeProductSection, LifePlanSection } from './components';

const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: '500px',
    backgroundColor: '#f5f5f5',
    padding: '20px'
  },
  form: {
    backgroundColor: '#ffffff',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    width: '100%',
    maxWidth: '1400px',
    overflow: 'hidden',
    border: '1px solid #e0e0e0'
  },
  header: {
    backgroundColor: '#f8f9fa',
    padding: '16px 24px',
    borderBottom: '1px solid #e0e0e0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center'
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#333',
    margin: 0
  },
  closeButton: {
    background: 'none',
    border: '1px solid #017DB7',
    fontSize: '13px',
    cursor: 'pointer',
    color: '#017DB7',
    padding: '6px 16px',
    borderRadius: '4px',
    fontWeight: '400',
    transition: 'all 0.2s ease'
  },
  tabsHeader: {
    display: 'flex',
    backgroundColor: '#f8f9fa'
  },
  tabBtn: active => ({
    padding: '15px 30px',
    border: 'none',
    background: active ? '#017DB7' : 'transparent',
    color: active ? '#fff' : '#6c757d',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    borderBottom: active ? '2px solid #017DB7' : '2px solid transparent',
    transition: 'all 0.2s ease'
  }),
  tabContent: {
    padding: '24px',
    backgroundColor: '#fff',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '24px'
  },
  columnHeader: {
    padding: '10px 16px',
    backgroundColor: '#017DB7',
    color: '#fff',
    fontSize: '13px',
    fontWeight: '500',
    textAlign: 'left',
    borderRadius: '4px',
    marginBottom: '16px'
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  formGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: 3
  },
  label: {
    fontSize: '13px',
    fontWeight: '500',
    color: '#333',
    marginBottom: '2px'
  },
  input: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '13px',
    transition: 'border-color 0.2s ease',
    backgroundColor: '#fff',
    fontFamily: 'inherit'
  },
  select: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    fontSize: '13px',
    backgroundColor: '#fff',
    cursor: 'pointer',
    transition: 'border-color 0.2s ease',
    fontFamily: 'inherit'
  },
  buttonGroup: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap'
  },
  button: isActive => ({
    padding: '7px 14px',
    border: `1px solid ${isActive ? '#2d3748' : '#d1d5db'}`,
    borderRadius: '4px',
    backgroundColor: isActive ? '#2d3748' : '#fff',
    color: isActive ? '#fff' : '#374151',
    fontSize: '13px',
    fontWeight: '400',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    justifyContent: 'center',
    ':hover': {
      borderColor: isActive ? '#2d3748' : '#9ca3af',
      backgroundColor: isActive ? '#1a202c' : '#f9fafb'
    }
  }),
  submitButton: {
    padding: '10px 32px',
    backgroundColor: '#017DB7',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s ease',
    ':hover': {
      backgroundColor: '#016a9d'
    }
  },
  submitButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '16px 24px',
    backgroundColor: '#fff',
    borderTop: '1px solid #e0e0e0'
  },
  carriersButton: {
    padding: '8px 12px',
    border: '1px solid #ccc',
    borderRadius: '4px',
    backgroundColor: '#fff',
    fontSize: '13px',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'border-color 0.2s ease',
    width: '100%'
  },
  riderButton: isActive => ({
    padding: '7px 14px',
    border: `1px solid ${isActive ? '#2d3748' : '#e0e0e0'}`,
    borderRadius: '20px',
    backgroundColor: isActive ? '#2d3748' : '#fff',
    color: isActive ? '#fff' : '#374151',
    fontSize: '13px',
    fontWeight: '400',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    whiteSpace: 'nowrap'
  }),
  uwListWrap: { margin: '16px 0' },
  uwListHeader: { display: 'flex', alignItems: 'center', marginBottom: 8 },
  uwListItem: {
    background: '#f1f3f6',
    borderRadius: 4,
    padding: '10px 16px',
    marginBottom: 8,
    display: 'flex',
    alignItems: 'center',
    boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
    cursor: 'pointer'
  },
  uwCarrier: {
    fontWeight: 500,
    color: '#2a3b4d',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  uwPill: (bg, color) => ({
    background: bg,
    borderRadius: 4,
    padding: '2px 8px',
    fontWeight: 400,
    color,
    marginLeft: 8,
    fontSize: 12,
    flexShrink: 0,
    lineHeight: 1.5
  }),
  tableRatingPill: {
    // distinct styling for table rating
    background: '#d1ecf1',
    color: '#0c5460'
  },
  modalBackdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000
  },
  modalCard: {
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
    maxWidth: 420,
    width: '90%',
    padding: 20
  },
  uwClassesModal: {
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
    maxWidth: 600,
    width: '90%',
    padding: 24,
    maxHeight: '80vh',
    overflow: 'auto'
  },
  uwClassesModalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 12,
    borderBottom: '1px solid #e0e0e0'
  },
  uwClassesModalTitle: {
    fontSize: 18,
    fontWeight: 500,
    color: '#333',
    margin: 0
  }
};

const toggleItem = (arr, item) => (arr.includes(item) ? arr.filter(x => x !== item) : [...arr, item]);

const isNonEmpty = arr => Array.isArray(arr) && arr.length > 0;

const TABLE_RATINGS = Array.from({ length: 22 }, (_, i) => `Table ${String.fromCharCode(65 + i)}`); // "Table A" through "Table V"

const removeTableRatings = healthClassArr => (Array.isArray(healthClassArr) ? healthClassArr.filter(item => !TABLE_RATINGS.includes(item)) : []);

const ensureTableRatedOn = (healthClassArr, nextShowTableRatings) => {
  const withoutTables = removeTableRatings(healthClassArr);
  return {
    healthClass: withoutTables,
    showTableRatings: nextShowTableRatings
  };
};

function DeactivatedModal({ open }) {
  if (!open) return null;
  return (
    <div aria-modal='true' role='dialog' style={styles.modalBackdrop}>
      <div style={{ ...styles.modalCard, textAlign: 'center' }}>
        <h3 style={{ fontSize: 16, color: '#333' }}>This Quoter is Inactive. Please contact your strategic account manager for assistance</h3>
      </div>
    </div>
  );
}

function ConfirmModal({ open, message, onCancel, onConfirm }) {
  if (!open) return null;
  return (
    <div aria-modal='true' role='dialog' style={styles.modalBackdrop}>
      <div style={styles.modalCard}>
        <h3 style={{ margin: '0 0 8px', fontSize: 18, color: '#333' }}>Please confirm</h3>
        <p style={{ margin: '0 0 16px', color: '#444' }}>{message}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button onClick={onCancel} type='button' variant='secondary'>
            Cancel
          </Button>
          <Button onClick={onConfirm} type='button' variant='primary'>
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
}

ConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  onCancel: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired
};

function UnderwritingClassesModal({ open, items, onClose, onSelect, onReset }) {
  if (!open) return null;
  return (
    <div aria-modal='true' onClick={onClose} role='dialog' style={styles.modalBackdrop}>
      <div onClick={e => e.stopPropagation()} style={styles.uwClassesModal}>
        <div style={styles.uwClassesModalHeader}>
          <h3 style={styles.uwClassesModalTitle}>Underwriting Classes ({items.length})</h3>
          <Button onClick={onClose} style={{ fontSize: 12, padding: '4px 12px', height: '28px' }} type='button' variant='secondary'>
            Close
          </Button>
        </div>
        <UnderwritingList
          items={items}
          onReset={() => {
            onReset();
            onClose();
          }}
          onSelect={item => {
            onSelect(item);
            onClose();
          }}
        />
      </div>
    </div>
  );
}

UnderwritingClassesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  items: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  onReset: PropTypes.func.isRequired
};

function Tabs({ tabs, active, onChange }) {
  return (
    <div style={styles.tabsHeader}>
      {tabs.map(t => (
        <button key={t.id} onClick={() => onChange(t.id)} style={styles.tabBtn(active === t.id)} type='button'>
          {t.label}
        </button>
      ))}
    </div>
  );
}

Tabs.propTypes = {
  tabs: PropTypes.array.isRequired,
  active: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired
};

function UnderwritingList({ items, onReset, onSelect }) {
  return (
    <div style={styles.uwListWrap}>
      <div style={styles.uwListHeader}>
        <h4 style={{ fontWeight: 500, color: '#333', margin: 0 }}>Underwriting Classes</h4>
        <Button onClick={onReset} style={{ marginLeft: 12, fontSize: 12, padding: '4px 10px', height: '28px' }} type='button' variant='secondary'>
          Reset
        </Button>
      </div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((item, idx) => {
          // Detect table rating field variations
          const tableRatingRaw = item.table_rate_letter || item.tableRateLetter || item.table_rating || item.tableRating || item.table_rate || null;
          let tableRatingDisplay = null;
          if (tableRatingRaw) {
            const val = String(tableRatingRaw).trim();
            tableRatingDisplay = val.toLowerCase().startsWith('table') ? val : `Table ${val}`;
          }
          return (
            <li key={idx} onClick={() => onSelect(item)} style={styles.uwListItem}>
              <span style={styles.uwCarrier}>{item.carrier}</span>
              <span style={styles.uwPill('#e0e7ef', '#3a4d63')}>{item.underwritingClass}</span>
              {item.tobaccoStatus && <span style={styles.uwPill('#ffe0b2', '#a65c00')}>{item.tobaccoStatus}</span>}
              {tableRatingDisplay && <span style={{ ...styles.uwPill('#d1ecf1', '#0c5460'), ...styles.tableRatingPill }}>{tableRatingDisplay}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

UnderwritingList.propTypes = {
  items: PropTypes.array.isRequired,
  onReset: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired
};

const OptimizedQuoteForm = ({
  formData,
  onInputChange,
  onSubmit,
  onModalOpen,
  formType = 'annuity',
  lifeProductOptions = {},
  underwritingClasses,
  hasHealthAnalyzer = false,
  setUnderwritingClasses,
  solveType,
  alqGuides = [],
  alqCarriers = [],
  aaqCriteriaOptions,
  hasQuoteByPremium = false,
  uqData,
  loading
}) => {
  const jurisdictionOptions = STATES;

  const [confirm, setConfirm] = React.useState({
    open: false,
    message: '',
    onConfirm: () => { }
  });
  const [activeProductTab, setActiveProductTab] = React.useState('TERM');
  const [underwritingClass, setUnderwritingClass] = React.useState(null);
  const [toolboxModal, setToolboxModal] = React.useState(null);
  const [showFaceAmountPopup, setShowFaceAmountPopup] = React.useState(false);
  const [showUwClassesModal, setShowUwClassesModal] = React.useState(false);
  const maxFaceAmounts = 3;

  const { universal_quoter_details = [] } = uqData || {};

  const annuityQuoter = universal_quoter_details.find(detail => detail?.quoter_type === 1);
  const lifeQuoter = universal_quoter_details.find(detail => detail?.quoter_type === 2);

  const isAnnuityDeactivated = Boolean(annuityQuoter?.is_deactivated);
  const isLifeDeactivated = Boolean(lifeQuoter?.is_deactivated);

  // if the Table Rated panel is hidden, ensure no table letters remain selected.
  React.useEffect(() => {
    if (!formData.showTableRatings) {
      const current = formData.healthClass || [];
      const next = removeTableRatings(current);
      if (Array.isArray(current) && current.length !== next.length) {
        onInputChange('healthClass', next);
      }
    }
  }, [formData.showTableRatings, formData.healthClass]);

  // Sync activeProductTab from formData (for saved data from backend)
  React.useEffect(() => {
    if (formData?.activeProductTab && formData.activeProductTab !== activeProductTab) {
      setActiveProductTab(formData.activeProductTab);
    }
  }, [formData?.activeProductTab]);

  React.useEffect(() => {
    // When product types change, validate and adjust solve type if needed
    if (formType === 'annuity') {
      const availableOptions = getAnnuitySolveOptions();
      const currentSolveFor = formData.solveFor;

      // Check if current solve type is still valid
      const isCurrentSolveValid = availableOptions.some(option => option.value === currentSolveFor);

      // If current solve type is no longer valid, switch to Accumulation (default)
      if (!isCurrentSolveValid && availableOptions.length > 0) {
        onInputChange('solveFor', 'Accumulation');
      }
    }
  }, [formData?.incomeType, formData?.solveFor, formData.productTypes, formType]);

  // Snackbar state for validation messages
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'warning' // 'error', 'warning', 'info', 'success'
  });

  // Close snackbar handler
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar({ ...snackbar, open: false });
  };

  // Show snackbar with message
  const showSnackbar = (message, severity = 'warning') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Handle Health Analyzer click with validation
  const handleHealthAnalyzerClick = () => {
    const dateOfBirthField = formType === 'annuity' ? formData.dateOfBirthAnnuity : formData.dateOfBirthLife;
    if (!dateOfBirthField) {
      showSnackbar('Please enter a Date of Birth before using Health Analyzer', 'warning');
      return;
    }
    onModalOpen('health-analyzer');
  }; // Validate form before submission
  const handleSubmit = () => {
    // Common validations - use tab-specific fields
    const dateOfBirthField = formType === 'annuity' ? formData.dateOfBirthAnnuity : formData.dateOfBirthLife;
    const genderField = formType === 'annuity' ? formData.genderAnnuity : formData.genderLife;
    const jurisdictionField = formType === 'annuity' ? formData.jurisdictionAnnuity : formData.jurisdictionLife;

    if (!dateOfBirthField) {
      showSnackbar('Please enter a Date of Birth', 'error');
      return;
    }

    if (!genderField) {
      showSnackbar('Please select a Gender', 'error');
      return;
    }

    if (!jurisdictionField) {
      showSnackbar('Please select a Jurisdiction', 'error');
      return;
    }

    // Life-specific validations
    if (formType === 'life') {
      if (!formData?.healthClass || formData?.healthClass?.length === 0) {
        showSnackbar('Please select at least one Health Class', 'error');
        return;
      }

      if (formData?.solveFor === 'Premium' && (!formData?.faceAmounts || formData.faceAmounts.length === 0)) {
        showSnackbar('Please enter at least one Face Amount', 'error');
        return;
      }

      if (formData?.solveFor === 'Face Amount' && (!formData?.premiumLife || formData.premiumLife.length === 0)) {
        showSnackbar('Please enter a Premium Amount', 'error');
        return;
      }

      if (formData?.tobacco === undefined || formData?.tobacco === null) {
        showSnackbar('Please select a Tobacco Status', 'error');
        return;
      }

      if (!formData?.selectedCarriers || formData.selectedCarriers.length === 0) {
        showSnackbar('Please select at least one Carrier', 'error');
        return;
      }

      if (!formData?.selectedProductTypes || formData.selectedProductTypes.length === 0) {
        showSnackbar('Please select at least one Product', 'error');
        return;
      }
    }

    // Annuity-specific validations
    if (formType === 'annuity') {
      if (!formData?.premiumAnnuity) {
        showSnackbar('Please enter a Premium Amount', 'error');
        return;
      }

      if (!formData?.selectedCarriers || formData.selectedCarriers.length === 0) {
        showSnackbar('Please select at least one Carrier', 'error');
        return;
      }
      if (!formData?.productTypes || formData.productTypes.length === 0) {
        showSnackbar('Please select at least one Product Type', 'error');
        return;
      }
    }

    // If all validations pass, proceed with submission
    onSubmit();
  };

  const toolboxStyles = {
    tool_modal_content: {
      padding: 40
    },
    guide: {
      display: 'block',
      lineHeight: '28px',
      fontSize: '14px',
      color: '#0193d7',
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      textDecoration: 'none'
    },
    icon: {
      color: '#999',
      fontSize: 20,
      marginRight: 8
    }
  };
  // Ensure activeProductTab is always set to a valid tab with products
  React.useEffect(() => {
    if (formType === 'life' && lifeProductOptions) {
      const availableTabs = [
        { id: 'TERM', products: lifeProductOptions.term_products || [] },
        { id: 'GUL', products: lifeProductOptions.guaranteed_life_products || [] },
        { id: 'IUL', products: lifeProductOptions.iul_products || [] },
        { id: 'ROP', products: lifeProductOptions.return_of_premium_products || [] },
        { id: 'OTHER', products: lifeProductOptions.other_products || [] }
      ].filter(t => t.products.length > 0);

      // If current tab has no products, switch to first available tab
      const currentTabHasProducts = availableTabs.some(t => t.id === activeProductTab);
      if (!currentTabHasProducts && availableTabs.length > 0) {
        setActiveProductTab(availableTabs[0].id);
      }
    }
  }, [formType, lifeProductOptions, activeProductTab]);

  React.useEffect(() => {
    if ((formData.faceAmounts?.length || 0) >= maxFaceAmounts && showFaceAmountPopup) {
      setShowFaceAmountPopup(false);
    }
  }, [formData.faceAmounts, showFaceAmountPopup]); // Initialize with default values based on formType
  React.useEffect(() => {
    if (formType === 'annuity') {
      if (!formData.premiumAnnuity) {
        onInputChange('premiumAnnuity', '150000');
      }
    } else if (formType === 'life') {
      if (!formData.faceAmounts || formData.faceAmounts.length === 0) {
        onInputChange('faceAmounts', [150000]);
      }
      if (!formData.solveFor) {
        setTimeout(() => {
          onInputChange('solveFor', 'Premium');
        }, 0);
      }
    }
  }, [formType]);

  // Initialize child_rider_units with default value of '0'
  React.useEffect(() => {
    if (formType === 'life' && !formData.child_rider_units) {
      onInputChange('child_rider_units', '0');
    }
  }, [formType]);

  const askConfirm = (message, action) => {
    setConfirm({
      open: true,
      message,
      onConfirm: () => {
        action();
        setConfirm({ open: false, message: '', onConfirm: () => { } });
      }
    });
  };

  const confirmIfNeeded = kind => {
    const hasResults = isNonEmpty(underwritingClasses);
    if (!hasResults) {
      if (kind === 'reset') setUnderwritingClasses([]);
      if (kind === 'select-carriers') onModalOpen('carriers');
      return;
    }
    if (kind === 'reset') {
      askConfirm('Reset Health Class?', () => setUnderwritingClasses([]));
    } else if (kind === 'select-carriers') {
      askConfirm('Changing carriers will discard current health analysis results. Continue?', () => {
        // Clear Health Analyzer results when user chooses to change carriers
        setUnderwritingClasses([]);
        if (typeof setUnderwritingClasses === 'function') {
          setUnderwritingClasses([]);
        }
        onModalOpen('carriers');
      });
    }
  };

  const getAnnuitySolveOptions = () => {
    const baseOptions = [
      { value: 'Accumulation', label: 'Accumulation' },
      { value: 'Contribution', label: 'Contribution' }
    ];

    const gmbOptions = [
      { value: 'Income', label: 'Rider Income' },
      { value: 'GMBIncome', label: 'Annuitization' }
    ];

    const selectedTypes = formData.productTypes || [];

    const hasFAorMYGA = selectedTypes.some(type => type === 'FA' || type === 'MYGA');
    const hasFIA = selectedTypes.includes('FIA');
    const allowedSolveTypes = solveType !== null && solveType !== undefined ? mapSolveTypeEnumToStrings(Array.isArray(solveType) ? solveType : [solveType]) : null;

    //For FA and MYGA
    if (hasFAorMYGA) {
      if (allowedSolveTypes && allowedSolveTypes.length > 0) {
        const filteredOptions = baseOptions.filter(option => allowedSolveTypes.includes(option.value));
        return filteredOptions.length > 0 ? filteredOptions : [];
      }
      return baseOptions;
    }

    // If ONLY FIA is selected (no FA/MYGA)
    if (hasFIA && !hasFAorMYGA) {
      const allOptions = [...gmbOptions, ...baseOptions];
      if (allowedSolveTypes && allowedSolveTypes.length > 0) {
        return allOptions.filter(option => allowedSolveTypes.includes(option.value));
      }
      return allOptions;
    }

    // Default: No product type selected - show base options filtered by solve_type
    if (allowedSolveTypes && allowedSolveTypes.length > 0) {
      return baseOptions.filter(option => allowedSolveTypes.includes(option.value));
    }
    return baseOptions;
  };

  const onHandleHealthClassSelect = (formData, health) => {
    const current = formData?.healthClass || [];
    if (health === 'Table Rated') {
      const nextShow = !formData?.showTableRatings;
      if (!nextShow && current.some(h => NON_TOBACCO?.includes(h))) {
        const { healthClass, showTableRatings } = ensureTableRatedOn(current, false);
        onInputChange('healthClass', healthClass);
        onInputChange('showTableRatings', showTableRatings);
        return;
      }

      // Turning on: ensure prior table ratings are cleared, then show panel.
      const { healthClass, showTableRatings } = ensureTableRatedOn(current, true);
      onInputChange('healthClass', healthClass);
      onInputChange('showTableRatings', showTableRatings);
    } else {
      const updated = current.includes(health) ? current.filter(h => h !== health) : [...current, health];
      onInputChange('healthClass', updated.length > 0 ? updated : [health]);
    }
  };

  // Render functions for the new tabbed layout
  const renderProposedInsuredTab = () => (
    <div style={styles.formGrid}>
      {/* Proposed Insured Name Fields */}
      <ProposedInsuredSection formData={formData} onInputChange={onInputChange} styles={styles} formType={formType} />

      {/* Common Demographics - Split by form type */}
      {formType === 'annuity' ? (
        <CommonFields formData={formData} onInputChange={onInputChange} styles={styles} formType={formType} />
      ) : (
        <LifeDemographicsSection
          formData={formData}
          onInputChange={onInputChange}
          hasHealthAnalyzer={hasHealthAnalyzer}
          handleHealthAnalyzerClick={handleHealthAnalyzerClick}
          onHandleHealthClassSelect={onHandleHealthClassSelect}
          underwritingClasses={underwritingClasses}
          setShowUwClassesModal={setShowUwClassesModal}
          isNonEmpty={isNonEmpty}
          styles={styles}
        />
      )}
    </div>
  );
  const renderProductTab = () => {
    if (formType === 'life') {
      return (
        <LifeProductSection
          formData={formData}
          onInputChange={onInputChange}
          lifeProductOptions={lifeProductOptions}
          activeProductTab={activeProductTab}
          setActiveProductTab={setActiveProductTab}
          jurisdictionOptions={jurisdictionOptions}
          confirmIfNeeded={confirmIfNeeded}
          styles={styles}
        />
      );
    } else {
      // Annuity product tab - Using extracted component
      return <AnnuityProductSection formData={formData} onInputChange={onInputChange} onModalOpen={onModalOpen} styles={styles} aaqCriteriaOptions={aaqCriteriaOptions} />;
    }
  };
  const renderPlanTab = () => {
    const lifeSolveOptions = hasQuoteByPremium ? ['Face Amount', 'Premium'] : ['Face Amount'];
    const lifeSolveFor = lifeSolveOptions?.includes(formData?.solveFor) ? formData?.solveFor : lifeSolveOptions[0];

    const annuitySolveTypes = getAnnuitySolveOptions();
    const annuitySolveFor = formData.solveFor || annuitySolveTypes[0]?.value || 'Accumulation';

    // Check if any selected products are IUL
    const hasIULSelected =
      formType === 'life' &&
      (formData.selectedProductTypes || []).some(pt => {
        const productLower = (pt || '').toLowerCase();
        return productLower.includes('iul') || productLower.includes('indexed universal');
      });

    if (formType === 'life') {
      return (
        <LifePlanSection
          formData={formData}
          onInputChange={onInputChange}
          hasQuoteByPremium={hasQuoteByPremium}
          showFaceAmountPopup={showFaceAmountPopup}
          setShowFaceAmountPopup={setShowFaceAmountPopup}
          styles={styles}
        />
      );
    }

    // Annuity Plan Section
    return (
      <div style={styles.formGrid}>
        {/* Solve For - Annuity */}
        <div style={styles.formGroup} data-itemid='solve-for-wrapper'>
          <div style={styles.row} data-itemid='solve-for-label-wrapper'>
            <label style={styles.label} data-itemid='solve-for-label'>
              Solve For
            </label>
            <label style={{ ...styles.label, color: 'red' }} data-itemid='solve-for-label-required'>
              *
            </label>
          </div>
          <Select
            onChange={e => onInputChange('solveFor', e.target.value)}
            options={annuitySolveTypes}
            placeholder='Select Solve Type'
            value={annuitySolveFor}
            data-itemid='annuity-solve-for-select'
          />
        </div>

        {/* Annuity Plan Fields */}
        <AnnuityPlanSection formData={formData} onInputChange={onInputChange} styles={styles} />
      </div>
    );
  };

  const isQuoterDeactivated = isAnnuityDeactivated && isLifeDeactivated;
  return (
    <form style={styles.container} role='form' onSubmit={e => e.preventDefault()}>
      <div style={styles.form}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.headerTitle}>Quoting</h1>
          {/* <button style={styles.closeButton} onClick={() => { }}>
                        Close
                    </button> */}
        </div>
        {/* Three-column layout */}
        <div style={styles.tabContent}>
          {/* Column 1: Proposed Insured */}
          <div style={styles.column}>
            <div style={styles.columnHeader}>Proposed Insured</div>
            {renderProposedInsuredTab()}
          </div>

          {/* Column 2: Product */}
          <div style={styles.column}>
            <div style={styles.columnHeader}>Product</div>
            {renderProductTab()}
          </div>

          {/* Column 3: Plan */}
          <div style={styles.column}>
            <div style={styles.columnHeader}>Plan</div>
            {renderPlanTab()}
          </div>
        </div>
        {/* Submit Button */}
        <div style={styles.submitButtonContainer} data-itemid='submit-button-container'>
          {/* TOOLBOX BUTTON*/}
          {formType === 'life' && (
            <button
              onClick={() => setToolboxModal('menu')}
              onMouseOut={e => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
              }}
              onMouseOver={e => {
                e.currentTarget.style.backgroundColor = '#e0e0e0';
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#f0f0f0',
                color: '#333',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                marginRight: 'auto',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
              type='button'
              data-itemid='toolbox-button'
            >
              <Icon path={mdiToolbox} size={0.8} />
              Toolbox
            </button>
          )}

          <button onClick={handleSubmit} style={styles.submitButton} type='button' data-itemid='get-quotes-button'>
            Get Quotes
          </button>
        </div>
      </div>

      {/* Quoter Deactivated Popup Modal  */}
      {!loading && <DeactivatedModal open={isQuoterDeactivated} />}

      <ConfirmModal message={confirm.message} onCancel={() => setConfirm({ open: false, message: '', onConfirm: () => { } })} onConfirm={confirm.onConfirm} open={confirm.open} />

      {/* Underwriting Classes Modal - Shows all health analyzer results */}
      <UnderwritingClassesModal
        items={underwritingClasses || []}
        onClose={() => setShowUwClassesModal(false)}
        onReset={() => setUnderwritingClasses([])}
        onSelect={setUnderwritingClass}
        open={showUwClassesModal}
      />

      {/* Health Analyzer Detail Modal - Shows reasoning when a health analyzer result is selected */}
      {underwritingClass && <HealthAnalyzerDetailModal data={underwritingClass} onClose={() => setUnderwritingClass(null)} />}

      {formType === 'life' && (
        <>
          {/* Toolbox Menu Modal */}
          {toolboxModal === 'menu' && (
            <ButtonMenuModal
              buttons={[
                {
                  id: 'button-needs-analysis',
                  onClick: () => setToolboxModal('needs_analysis'),
                  label: 'Needs Analysis Calculator',
                  icon: 'mdi-calculator'
                },
                {
                  id: 'button-underwriting-guides',
                  onClick: () => setToolboxModal('underwriting_guides'),
                  label: 'Underwriting Guides',
                  icon: 'mdi-file-document-outline'
                }
                // Add more buttons here as needed
              ]}
              id='modal-tools'
              onClose={() => setToolboxModal(null)}
              title='Toolbox'
            />
          )}
          {/* Needs Analysis Calculator Modal */}
          {toolboxModal === 'needs_analysis' && (
            <NeedsAnalysisModal
              agent_id={null}
              maximum_face_amount={10000000}
              minimum_face_amount={50000}
              onBackToTools={() => setToolboxModal('menu')}
              onClose={() => setToolboxModal(null)}
              onQuoteWithCoverage={calculatedCoverage => {
                // Add the calculated coverage to face amounts
                const currentAmounts = formData.faceAmounts || [];
                if (currentAmounts.length < 3) {
                  onInputChange('faceAmounts', [...currentAmounts, calculatedCoverage]);
                } else {
                  // Replace the last one if at max
                  onInputChange('faceAmounts', [...currentAmounts.slice(0, 2), calculatedCoverage]);
                }
                setToolboxModal(null);
              }}
            />
          )}

          {/* Underwriting Guides Modal */}
          {toolboxModal === 'underwriting_guides' && (
            <Modal
              buttons={[
                {
                  color: '#e5e5e5',
                  fontColor: '#444',
                  children: 'Back To Tools',
                  onClick: () => setToolboxModal('tools')
                }
              ]}
              id='modal-underwriting-guides'
              maxWidth={400}
              onClose={() => setToolboxModal(null)}
              title='Carrier Underwriting Guides'
            >
              <div id='underwriting-guides-content' style={toolboxStyles.tool_modal_content}>
                {alqGuides && alqGuides.length > 0 ? (
                  alqGuides
                    .filter(g => g.states?.includes(formData.jurisdictionLife || formData.jurisdictionAnnuity || 'AL'))
                    .sort((a, b) => {
                      const carrier_a = alqCarriers.find(c => c.id === a.carrier_id);
                      const carrier_b = alqCarriers.find(c => c.id === b.carrier_id);

                      if (carrier_a && carrier_b) {
                        return carrier_a.name.toLowerCase() > carrier_b.name.toLowerCase() ? 1 : -1;
                      }
                      return 0;
                    })
                    .map(g => {
                      const pdf = g.pdf_file || {};
                      const carrier = alqCarriers.find(c => g.carrier_id === c.id);

                      return carrier ? (
                        <a href={pdf.url} id={`guide-${carrier.id}`} key={carrier.id} rel='noopener noreferrer' style={toolboxStyles.guide} target='_blank'>
                          <i className='mdi mdi-file-pdf-outline' style={toolboxStyles.icon} />
                          {carrier.name}
                        </a>
                      ) : null;
                    })
                ) : (
                  <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>No underwriting guides available for the selected state.</div>
                )}
              </div>
            </Modal>
          )}
        </>
      )}
      {/* Snackbar for validation messages */}
      <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'right' }} autoHideDuration={6000} onClose={handleCloseSnackbar} open={snackbar.open}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }} variant='filled'>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </form>
  );
};

OptimizedQuoteForm.propTypes = {
  formData: PropTypes.object.isRequired,
  formType: PropTypes.oneOf(['annuity', 'life', 'ltc']),
  onInputChange: PropTypes.func.isRequired,
  onModalOpen: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  lifeProductOptions: PropTypes.object,
  underwritingClasses: PropTypes.arrayOf(PropTypes.object),
  setUnderwritingClasses: PropTypes.func.isRequired,
  hasHealthAnalyzer: PropTypes.bool,
  solveType: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
  alqGuides: PropTypes.array,
  alqCarriers: PropTypes.array,
  aaqCriteriaOptions: PropTypes.object.isRequired,
  hasQuoteByPremium: PropTypes.bool,
  uqData: PropTypes.object,
  loading: PropTypes.bool
};

export default OptimizedQuoteForm;
